import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Property } from "@/api/properties";
import { RootState } from "../store";

// The pages that use the multi-select picker. They share one selection, so
// picking properties on one carries over to the others.
export type MultiSelectPage =
  | "activeLeads"
  | "groupPricingCalculator"
  | "groupPricingRequests"
  | "pricingAudit";

interface PropertyState {
  selectedProperty: Property | null;
  selectedMultiProperties: Property[];
  // One-time-per-session guard: once the portfolio auto-select has run, an
  // explicit clear-to-empty afterward must stick — never re-run.
  hasAutoSelectedPortfolio: boolean;
}

// Persisted to sessionStorage (not just kept in memory) so an explicit
// clear-to-empty — or the one-time auto-select having already run — survives
// a page reload within the same browser tab, not just SPA navigation. A
// genuinely new tab/session starts fresh since sessionStorage doesn't carry over.
export const MULTI_SELECT_STORAGE_KEY = "property:multiSelectState";

interface PersistedMultiSelectState {
  selectedMultiProperties: Property[];
  hasAutoSelectedPortfolio: boolean;
}

const defaultMultiSelectState: PersistedMultiSelectState = {
  selectedMultiProperties: [],
  hasAutoSelectedPortfolio: false,
};

function loadPersistedMultiSelectState(): PersistedMultiSelectState {
  try {
    const raw = sessionStorage.getItem(MULTI_SELECT_STORAGE_KEY);
    if (!raw) return defaultMultiSelectState;
    const parsed = JSON.parse(raw);
    return {
      selectedMultiProperties: Array.isArray(parsed?.selectedMultiProperties)
        ? parsed.selectedMultiProperties
        : [],
      hasAutoSelectedPortfolio: parsed?.hasAutoSelectedPortfolio === true,
    };
  } catch {
    return defaultMultiSelectState;
  }
}

const initialState: PropertyState = {
  selectedProperty: null,
  ...loadPersistedMultiSelectState(),
};

const propertySlice = createSlice({
  name: "property",
  initialState,
  reducers: {
    setSelectedProperty: (state, action: PayloadAction<Property | null>) => {
      state.selectedProperty = action.payload;
    },
    clearSelectedProperty: (state) => {
      state.selectedProperty = null;
    },
    setSelectedMultiProperties: (state, action: PayloadAction<Property[]>) => {
      state.selectedMultiProperties = action.payload;
    },
    clearSelectedMultiProperties: (state) => {
      state.selectedMultiProperties = [];
    },
    markPortfolioAutoSelected: (state) => {
      state.hasAutoSelectedPortfolio = true;
    },
  },
});

export const {
  setSelectedProperty,
  clearSelectedProperty,
  setSelectedMultiProperties,
  clearSelectedMultiProperties,
  markPortfolioAutoSelected,
} = propertySlice.actions;
export const selectSelectedProperty = (state: RootState) =>
  state.property.selectedProperty;
export const selectSelectedMultiProperties = (state: RootState) =>
  state.property.selectedMultiProperties;
/** Whether the one-time portfolio auto-select has already run this session. */
export const selectHasAutoSelectedPortfolio = (state: RootState) =>
  state.property.hasAutoSelectedPortfolio;
export default propertySlice.reducer;
